import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Globe, Shield, Server, Database, Key, AlertTriangle, Target,
  User, Crown, Lock, Bug, Zap, Play, Code, FileText, ChevronDown,
  ChevronUp, Terminal, Crosshair, Network, Cpu, HardDrive, Wifi,
  Eye, Download, ExternalLink, Activity
} from 'lucide-react';

// Custom Node Types
const VulnerabilityNode = ({ data, selected }) => {
  const severityColors = {
    critical: { bg: 'from-red-600/30 to-red-800/20', border: 'border-red-500', text: 'text-red-400', glow: 'shadow-red-500/30' },
    high: { bg: 'from-orange-600/30 to-orange-800/20', border: 'border-orange-500', text: 'text-orange-400', glow: 'shadow-orange-500/30' },
    medium: { bg: 'from-yellow-600/30 to-yellow-800/20', border: 'border-yellow-500', text: 'text-yellow-400', glow: 'shadow-yellow-500/30' },
    low: { bg: 'from-blue-600/30 to-blue-800/20', border: 'border-blue-500', text: 'text-blue-400', glow: 'shadow-blue-500/30' },
    info: { bg: 'from-gray-600/30 to-gray-800/20', border: 'border-gray-500', text: 'text-gray-400', glow: 'shadow-gray-500/30' },
  };

  const colors = severityColors[data.severity] || severityColors.info;

  return (
    <div className={`relative bg-gradient-to-br ${colors.bg} rounded-xl border-2 ${colors.border} p-4 min-w-[220px] max-w-[280px] shadow-lg ${selected ? `shadow-xl ${colors.glow}` : ''} transition-all hover:scale-[1.02]`}>
      <Handle type="target" position={Position.Left} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-cyan-300" />
      <Handle type="source" position={Position.Right} className="!bg-brand-green !w-3 !h-3 !border-2 !border-green-300" />

      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-black/40 ${colors.border} border`}>
          <Bug className={`w-5 h-5 ${colors.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} font-bold uppercase border ${colors.border}`}>
              {data.severity}
            </span>
            {data.cvss && (
              <span className="text-xs text-gray-400">CVSS: {data.cvss}</span>
            )}
          </div>
          <h4 className="text-white font-semibold text-sm leading-tight truncate" title={data.title}>
            {data.title?.substring(0, 35)}{data.title?.length > 35 ? '...' : ''}
          </h4>
          {data.cve && (
            <span className="text-xs text-brand-green font-mono">{data.cve}</span>
          )}
        </div>
      </div>

      {data.exploitable && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
          <Zap className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
  );
};

const TargetNode = ({ data, selected }) => {
  return (
    <div className={`bg-gradient-to-br from-cyan-600/30 to-cyan-800/20 rounded-xl border-2 border-cyan-500 p-4 min-w-[200px] shadow-lg ${selected ? 'shadow-xl shadow-cyan-500/30' : ''} transition-all`}>
      <Handle type="source" position={Position.Right} className="!bg-brand-green !w-3 !h-3 !border-2 !border-green-300" />

      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-black/40 border border-cyan-500">
          <Target className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h4 className="text-white font-bold text-sm">CIBLE</h4>
          <p className="text-cyan-400 text-xs font-mono">{data.target}</p>
        </div>
      </div>
    </div>
  );
};

const ExploitNode = ({ data, selected, onClick }) => {
  return (
    <div
      onClick={() => data.onExecute && data.onExecute(data)}
      className={`bg-gradient-to-br from-purple-600/30 to-purple-800/20 rounded-xl border-2 border-purple-500 p-4 min-w-[200px] shadow-lg ${selected ? 'shadow-xl shadow-purple-500/30' : ''} transition-all cursor-pointer hover:border-purple-400`}
    >
      <Handle type="target" position={Position.Left} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-cyan-300" />
      <Handle type="source" position={Position.Right} className="!bg-brand-green !w-3 !h-3 !border-2 !border-green-300" />

      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-black/40 border border-purple-500">
          <Code className="w-5 h-5 text-purple-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-white font-bold text-sm">{data.label}</h4>
            {data.ready && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/50">
                Ready
              </span>
            )}
          </div>
          <p className="text-purple-400 text-xs">{data.type}</p>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button className="flex-1 px-3 py-1.5 bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded-lg text-xs hover:bg-purple-500/30 transition-all flex items-center justify-center gap-1">
          <Play className="w-3 h-3" /> Tester
        </button>
        <button className="px-3 py-1.5 bg-black/30 text-gray-400 border border-gray-600 rounded-lg text-xs hover:border-gray-500 transition-all">
          <FileText className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

const ActionNode = ({ data, selected }) => {
  const typeConfig = {
    reconnaissance: { icon: Eye, color: 'cyan', label: 'Reconnaissance' },
    scanning: { icon: Activity, color: 'blue', label: 'Scanning' },
    exploitation: { icon: Zap, color: 'red', label: 'Exploitation' },
    privilege_escalation: { icon: Crown, color: 'yellow', label: 'Privilege Escalation' },
    lateral_movement: { icon: Network, color: 'orange', label: 'Lateral Movement' },
    persistence: { icon: HardDrive, color: 'purple', label: 'Persistence' },
    exfiltration: { icon: Download, color: 'pink', label: 'Exfiltration' },
  };

  const config = typeConfig[data.type] || { icon: Terminal, color: 'gray', label: data.type };
  const IconComponent = config.icon;

  return (
    <div className={`bg-gradient-to-br from-${config.color}-600/20 to-${config.color}-800/10 rounded-xl border-2 border-${config.color}-500/50 p-3 min-w-[180px] shadow-lg ${selected ? 'shadow-xl' : ''} transition-all`}>
      <Handle type="target" position={Position.Left} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-cyan-300" />
      <Handle type="source" position={Position.Right} className="!bg-brand-green !w-3 !h-3 !border-2 !border-green-300" />

      <div className="flex items-center gap-2">
        <IconComponent className={`w-4 h-4 text-${config.color}-400`} />
        <span className={`text-${config.color}-400 text-xs font-medium`}>{config.label}</span>
      </div>
      <p className="text-white text-sm font-medium mt-1">{data.label}</p>
      {data.mitre && (
        <span className="text-xs text-gray-500 font-mono">{data.mitre}</span>
      )}
    </div>
  );
};

const ObjectiveNode = ({ data, selected }) => {
  return (
    <div className={`bg-gradient-to-br from-red-600/40 to-red-800/30 rounded-xl border-2 border-red-500 p-4 min-w-[200px] shadow-lg ${selected ? 'shadow-xl shadow-red-500/50' : ''} transition-all`}>
      <Handle type="target" position={Position.Left} className="!bg-cyan-500 !w-3 !h-3 !border-2 !border-cyan-300" />

      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-red-500/30 border border-red-500 animate-pulse">
          <Lock className="w-5 h-5 text-red-400" />
        </div>
        <div>
          <h4 className="text-white font-bold text-sm">OBJECTIF</h4>
          <p className="text-red-400 text-xs">{data.label}</p>
        </div>
      </div>
    </div>
  );
};

const nodeTypes = {
  vulnerability: VulnerabilityNode,
  target: TargetNode,
  exploit: ExploitNode,
  action: ActionNode,
  objective: ObjectiveNode,
};

// Main Component
const AttackTreeWorkflow = ({
  vulnerabilities = [],
  workspaceId = null,
  target = null,
  onExecuteExploit = null,
  onSelectVulnerability = null,
  compact = false
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [attackPaths, setAttackPaths] = useState([]);
  const [loading, setLoading] = useState(false);

  // Generate attack tree from vulnerabilities
  const generateAttackTree = useCallback(() => {
    if (!vulnerabilities.length) return;

    setLoading(true);

    const newNodes = [];
    const newEdges = [];

    // Group vulnerabilities by severity and type
    const criticals = vulnerabilities.filter(v => v.severity === 'critical');
    const highs = vulnerabilities.filter(v => v.severity === 'high');
    const mediums = vulnerabilities.filter(v => v.severity === 'medium');

    // Create target node
    const targetHost = target || vulnerabilities[0]?.scan_target || 'Target';
    newNodes.push({
      id: 'target',
      type: 'target',
      position: { x: 50, y: 300 },
      data: { target: targetHost }
    });

    // Create vulnerability nodes in attack path order
    let yOffset = 50;
    let nodeIndex = 0;
    const vulnNodeMap = {};

    // Critical vulnerabilities (initial access)
    criticals.slice(0, 5).forEach((vuln, idx) => {
      const nodeId = `vuln-${vuln.id || idx}`;
      vulnNodeMap[vuln.id] = nodeId;

      newNodes.push({
        id: nodeId,
        type: 'vulnerability',
        position: { x: 350, y: yOffset + (idx * 140) },
        data: {
          title: vuln.title,
          severity: vuln.severity,
          cve: vuln.cve_id,
          cvss: vuln.cvss_score,
          exploitable: vuln.cvss_score >= 7,
          vulnerability: vuln
        }
      });

      // Connect target to critical vulns
      newEdges.push({
        id: `e-target-${nodeId}`,
        source: 'target',
        target: nodeId,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#ef4444', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' }
      });
      nodeIndex++;
    });

    // High vulnerabilities (exploitation)
    const highStartY = yOffset;
    highs.slice(0, 4).forEach((vuln, idx) => {
      const nodeId = `vuln-${vuln.id || 'h' + idx}`;
      vulnNodeMap[vuln.id] = nodeId;

      newNodes.push({
        id: nodeId,
        type: 'vulnerability',
        position: { x: 650, y: highStartY + (idx * 140) },
        data: {
          title: vuln.title,
          severity: vuln.severity,
          cve: vuln.cve_id,
          cvss: vuln.cvss_score,
          exploitable: vuln.cvss_score >= 7,
          vulnerability: vuln
        }
      });

      // Connect from critical vulns to high vulns
      if (criticals.length > 0) {
        const sourceNode = `vuln-${criticals[Math.min(idx, criticals.length - 1)].id || Math.min(idx, criticals.length - 1)}`;
        newEdges.push({
          id: `e-${sourceNode}-${nodeId}`,
          source: sourceNode,
          target: nodeId,
          type: 'smoothstep',
          style: { stroke: '#f97316', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#f97316' }
        });
      }
      nodeIndex++;
    });

    // Create exploit nodes for exploitable vulnerabilities
    const exploitableVulns = vulnerabilities.filter(v => v.cvss_score >= 7).slice(0, 3);
    exploitableVulns.forEach((vuln, idx) => {
      const exploitId = `exploit-${idx}`;
      const vulnNodeId = vulnNodeMap[vuln.id];

      if (vulnNodeId) {
        newNodes.push({
          id: exploitId,
          type: 'exploit',
          position: { x: 950, y: 100 + (idx * 180) },
          data: {
            label: `Exploit ${vuln.cve_id || 'Custom'}`,
            type: getExploitType(vuln),
            ready: true,
            vulnerability: vuln,
            onExecute: onExecuteExploit
          }
        });

        newEdges.push({
          id: `e-${vulnNodeId}-${exploitId}`,
          source: vulnNodeId,
          target: exploitId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#a855f7', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#a855f7' }
        });
      }
    });

    // Create action nodes (MITRE ATT&CK phases)
    const actions = [
      { id: 'action-recon', type: 'reconnaissance', label: 'Network Scan', mitre: 'T1046', x: 1250, y: 100 },
      { id: 'action-exploit', type: 'exploitation', label: 'Initial Access', mitre: 'T1190', x: 1250, y: 250 },
      { id: 'action-privesc', type: 'privilege_escalation', label: 'Privilege Escalation', mitre: 'T1068', x: 1250, y: 400 },
    ];

    actions.forEach((action, idx) => {
      newNodes.push({
        id: action.id,
        type: 'action',
        position: { x: action.x, y: action.y },
        data: {
          label: action.label,
          type: action.type,
          mitre: action.mitre
        }
      });

      // Connect exploits to actions
      if (exploitableVulns.length > 0 && idx === 1) {
        newEdges.push({
          id: `e-exploit-0-${action.id}`,
          source: 'exploit-0',
          target: action.id,
          type: 'smoothstep',
          style: { stroke: '#22c55e', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e' }
        });
      }
    });

    // Connect actions sequentially
    newEdges.push({
      id: 'e-action-recon-exploit',
      source: 'action-recon',
      target: 'action-exploit',
      type: 'smoothstep',
      style: { stroke: '#6b7280', strokeWidth: 1, strokeDasharray: '5,5' }
    });
    newEdges.push({
      id: 'e-action-exploit-privesc',
      source: 'action-exploit',
      target: 'action-privesc',
      type: 'smoothstep',
      style: { stroke: '#6b7280', strokeWidth: 1, strokeDasharray: '5,5' }
    });

    // Create objective node
    newNodes.push({
      id: 'objective',
      type: 'objective',
      position: { x: 1550, y: 250 },
      data: { label: 'Compromission Totale' }
    });

    newEdges.push({
      id: 'e-privesc-objective',
      source: 'action-privesc',
      target: 'objective',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#ef4444', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' }
    });

    setNodes(newNodes);
    setEdges(newEdges);
    setLoading(false);

    // Calculate attack paths
    calculateAttackPaths(newNodes, newEdges);
  }, [vulnerabilities, target, onExecuteExploit]);

  const getExploitType = (vuln) => {
    const title = (vuln.title || '').toLowerCase();
    if (title.includes('rce') || title.includes('remote code')) return 'RCE Exploit';
    if (title.includes('sql') || title.includes('injection')) return 'SQL Injection';
    if (title.includes('xss')) return 'XSS Payload';
    if (title.includes('auth') || title.includes('bypass')) return 'Auth Bypass';
    if (title.includes('buffer') || title.includes('overflow')) return 'Buffer Overflow';
    return 'Custom Exploit';
  };

  const calculateAttackPaths = (nodeList, edgeList) => {
    // Find all paths from target to objective
    const paths = [];
    const criticalPath = {
      name: 'Chemin Critique',
      probability: 85,
      steps: nodeList.filter(n => n.data.severity === 'critical').length + 2,
      risk: 'critical'
    };
    const alternativePath = {
      name: 'Chemin Alternatif',
      probability: 60,
      steps: nodeList.filter(n => n.data.severity === 'high').length + 3,
      risk: 'high'
    };

    if (criticalPath.steps > 2) paths.push(criticalPath);
    if (alternativePath.steps > 2) paths.push(alternativePath);

    setAttackPaths(paths);
  };

  useEffect(() => {
    generateAttackTree();
  }, [vulnerabilities, generateAttackTree]);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    if (node.data.vulnerability && onSelectVulnerability) {
      onSelectVulnerability(node.data.vulnerability);
    }
  }, [onSelectVulnerability]);

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#00ff41', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#00ff41' }
    }, eds));
  }, [setEdges]);

  // Stats
  const stats = useMemo(() => ({
    totalVulns: vulnerabilities.length,
    criticals: vulnerabilities.filter(v => v.severity === 'critical').length,
    highs: vulnerabilities.filter(v => v.severity === 'high').length,
    exploitable: vulnerabilities.filter(v => v.cvss_score >= 7).length,
    avgCvss: vulnerabilities.length > 0
      ? (vulnerabilities.reduce((sum, v) => sum + (v.cvss_score || 0), 0) / vulnerabilities.filter(v => v.cvss_score).length).toFixed(1)
      : 0
  }), [vulnerabilities]);

  if (loading) {
    return (
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-8 flex items-center justify-center" style={{ height: compact ? '400px' : '600px' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Generating attack tree...</p>
        </div>
      </div>
    );
  }

  if (!vulnerabilities.length) {
    return (
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-8 flex items-center justify-center" style={{ height: compact ? '300px' : '400px' }}>
        <div className="text-center">
          <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No vulnerabilities to generate attack tree</p>
          <p className="text-gray-500 text-sm mt-2">Select a workspace or scan with vulnerabilities</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-black rounded-xl border border-brand-green/30 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-brand-green/30 bg-brand-black-light">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-brand-green/20 border border-brand-green/50">
              <Network className="w-6 h-6 text-brand-green" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Arbre d'Attaque Interactif</h3>
              <p className="text-gray-400 text-sm">Workflow de possibilites d'exploitation - Style n8n</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Quick Stats */}
            <div className="flex gap-3 text-sm">
              <div className="px-3 py-1 rounded-lg bg-red-500/20 text-red-400 border border-red-500/50">
                {stats.criticals} Critiques
              </div>
              <div className="px-3 py-1 rounded-lg bg-orange-500/20 text-orange-400 border border-orange-500/50">
                {stats.highs} Hautes
              </div>
              <div className="px-3 py-1 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/50">
                {stats.exploitable} Exploitables
              </div>
            </div>

            <button
              onClick={() => setShowDetails(!showDetails)}
              className="px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-all flex items-center gap-2"
            >
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Details
            </button>
          </div>
        </div>

        {/* Attack Paths Summary */}
        {showDetails && attackPaths.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            {attackPaths.map((path, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${
                path.risk === 'critical' ? 'bg-red-500/10 border-red-500/50' : 'bg-orange-500/10 border-orange-500/50'
              }`}>
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${path.risk === 'critical' ? 'text-red-400' : 'text-orange-400'}`}>
                    {path.name}
                  </span>
                  <span className="text-xs text-gray-400">{path.steps} etapes</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${path.risk === 'critical' ? 'bg-red-500' : 'bg-orange-500'}`}
                      style={{ width: `${path.probability}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-400">{path.probability}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ReactFlow Canvas */}
      <div style={{ height: compact ? '400px' : '550px' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.3}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#1a1a1a" gap={20} size={1} />
          <Controls
            className="!bg-brand-black-light !border-brand-green/30 !rounded-lg"
            showInteractive={false}
          />
          <MiniMap
            nodeColor={(node) => {
              if (node.type === 'target') return '#06b6d4';
              if (node.type === 'objective') return '#ef4444';
              if (node.type === 'exploit') return '#a855f7';
              if (node.data?.severity === 'critical') return '#ef4444';
              if (node.data?.severity === 'high') return '#f97316';
              return '#6b7280';
            }}
            className="!bg-brand-black-light !border-brand-green/30 !rounded-lg"
            maskColor="rgba(0,0,0,0.8)"
          />
        </ReactFlow>
      </div>

      {/* Selected Node Details */}
      {selectedNode && selectedNode.data.vulnerability && (
        <div className="p-4 border-t border-brand-green/30 bg-brand-black-light">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-white font-bold">{selectedNode.data.vulnerability.title}</h4>
              <div className="flex items-center gap-3 mt-2 text-sm">
                {selectedNode.data.vulnerability.cve_id && (
                  <span className="text-brand-green font-mono">{selectedNode.data.vulnerability.cve_id}</span>
                )}
                <span className="text-gray-400">CVSS: {selectedNode.data.vulnerability.cvss_score || 'N/A'}</span>
                <span className="text-gray-400">Host: {selectedNode.data.vulnerability.host_id || 'N/A'}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onExecuteExploit && onExecuteExploit(selectedNode.data.vulnerability)}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all flex items-center gap-2"
              >
                <Play className="w-4 h-4" /> Tester Exploit
              </button>
              <button className="px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-all flex items-center gap-2">
                <FileText className="w-4 h-4" /> Details CVE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="p-4 border-t border-brand-green/30 bg-brand-black/50">
        <div className="flex items-center justify-center gap-6 text-xs text-gray-400 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-cyan-500/30 border border-cyan-500"></div>
            <span>Cible</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500/30 border border-red-500"></div>
            <span>Critique</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-500/30 border border-orange-500"></div>
            <span>Haute</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-purple-500/30 border border-purple-500"></div>
            <span>Exploit</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500/30 border border-green-500"></div>
            <span>Action MITRE</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-red-500" />
            <span>Exploitable (CVSS >= 7)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttackTreeWorkflow;
